<template>
	<div class="container">
		<table class="cart-items-table" cellpadding="0" cellspacing="0" border="0">
			<thead v-if="cartItemList.length > 0">
				<tr>
					<th></th>
					<th class="cart-item-heading">Item</th>
					<th class="cart-item-qty">Qty</th>
					<th>Item Price</th>
					<th>Total</th>
				</tr>
			</thead>
			<tbody v-if="cartItemList.length > 0">
				<tr v-for="item in cartItemList" :key="item.id">
					<td>
						<ux-btn circle color="primary" aria-label="Remove Item" title="Remove Item" @click="removeItem(item.id)"><i class="fas fa-times"/></ux-btn>
					</td>
					<td>
						<div class="cart-item-desc">
							<div class="cart-item-image">
								<a :href="`/product/${item.product.url_slug}`"><img v-if="item.image_url" :src="item.image_url"></a>
							</div>

							<div v-if="item.item_type == 'product'" class="cart-item-info">
								<a :href="`/product/${item.product.url_slug}`" class="cart-item-title">{{ item.product.name }}</a>
								
								<div class="item-info-line">
									<strong>Sizing</strong>
									{{ item | displayItemSize('width') }} &times; {{ item | displayItemSize('height') }}
								</div>

								<div class="item-info-line">
									<strong>Room</strong>
									{{ item.room }}
								</div>

								<div class="item-info-line">
									<strong>Control Position</strong>
									{{ item.headrail_controls | capitalize }}
								</div>

								<div class="item-info-line">
									<strong>Mount Position</strong>
									{{ item.mount_pos | capitalize }}
								</div>

								<div v-for="opt in item.options" :key="opt.id" class="item-info-line">
									<strong>{{ opt.type_name }}</strong>
									{{ opt.name }}
								</div>
							</div>

							<div v-else-if="item.item_type == 'sample'" class="cart-item-info">
								<a :href="`/product/${item.product.url_slug}`" class="cart-item-title">{{ item.product.name }}</a>

								<div class="item-info-line">
									<strong>Sample:</strong>
									{{ item.option.name }}
								</div>
							</div>

						</div>
					</td>
					
					<td class="cart-item-qty" :class="{ 'has-error': qtys[item.id] == 0 || isNaN(qtys[item.id]) }">
						<input type="text" class="ux-control is-inline" size="3" v-model.number="qtys[item.id]"><br>
					</td>

					<!-- item price w/ discounts -->					
					<td v-if="item.discounts" class="cart-item-price">
						<span class="original-price">{{ item.price_single | currency }}</span>
						{{ item.price_discounted | currency }}
					</td>

					<!-- item price w/o discounts -->
					<td v-else class="cart-item-price">
						{{ item.price_single | currency }}
					</td>

					<td class="cart-item-price">{{ item.price | currency }}</td>
				</tr>

				<tr>
					<td colspan="2"></td>
					<td class="cart-total-label">Total:</td>
					<td class="cart-total-amount">{{ totals.total | currency }}</td>
				</tr>
			</tbody>
			<tbody v-else>
				<td colspan="5">
					<div class="cart-empty-message">There are no items in your cart</div>
				</td>
			</tbody>
		</table>

		<div class="row">
			<div class="col-md-6">
				<ul class="ux-btn-list is-horz">
					<li><ux-btn color="outline-primary" @click="goBack">Add Another</ux-btn></li>
					<li><ux-btn color="outline-primary" href="/categories">Continue Browsing</ux-btn></li>
				</ul>
			</div>

			<div class="col-md-6">
				<ul class="ux-btn-list is-horz-end">
					<li><ux-btn color="outline-primary" :disabled="loading" @click="updateQtys">Update Quantities</ux-btn></li>
					<li><ux-btn color="primary" :disabled="loading" to="/checkout">Checkout</ux-btn></li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import filters from 'common/filters';
import api from 'frontend/api';
import alerts from 'common/alerts';

export default {
	mixins: [filters],

	filters: {
		displayItemSize(item, field)
		{
			return filters.filters.sizeDisplay({ 
				whole: item[`${field}_whole`],
				fractional: item[`${field}_fractional`]
			})
		}
	},

	data()
	{
		return {
			loading: false,
			qtys: {},
		}
	},

	computed: {
		...mapGetters('cart', ['cartItemList']),
		...mapState({
			items: state => state.cart.items,
			totals: state => state.cart.totals,
		})
	},

	methods: {
		...mapActions('cart', ['fetchCartItems']),

		async refreshCart()
		{
			this.loading = true;

			try
			{
				var qtys = {};

				// load items
				await this.fetchCartItems();

				// initialize qtys
				for(var item of this.cartItemList)
					qtys[item.id] = item.qty;

				this.qtys = qtys;
			}
			finally
			{
				this.loading = false;
			}
		},

		displayHeadrailSizes(item)
		{
			var parts = [];

			for(var i=0; i<item.headrail_count; i++)
				parts[i] = `${item.headrail_sizes[0]}"`;

			return parts.join(', ');
		},

		async removeItem(id)
		{
			if (!await alerts.confirm('Are you sure you want to remove this item?', 'Remove Item', 'question'))
				return;

			this.loading = false;

			try
			{
				// remove item from cart in server session
				await api.delete(`/cart/${id}`);

				// refresh cart data
				await this.refreshCart();
			}
			finally
			{
				this.loading = false;
			}
		},

		async updateQtys()
		{
			// validate qtys
			for(var id in this.qtys)
			{
				if (this.qtys[id] <= 0 || isNaN(this.qtys[id]))
				{
					alerts.show('Please make sure all quantities are numeric and greater than zero', 'Invalid Quantities', 'warning');
					return;
				}
			}

			this.loading = true;
			
			try
			{
				// update qtys on server
				await api.post('/cart/update_qtys', { qtys: this.qtys });

				// refresh cart
				await this.refreshCart();
			}
			finally
			{
				this.loading = false;
			}
		},
		goBack() {
			// Using window.history.back() for a non-SPA or if Vue Router is not being used
			// window.history.back();

			// Uncomment the line below and comment out the window.history.back() line if using Vue Router
			this.$router.go(-1);
		}
	},

	mounted()
	{
		this.refreshCart();
	}
}
</script>